import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { borderRadius, customSizes, palette, spaces } from '../../assets/styles/themes/tokens';
import { useTranslation } from 'react-i18next';
import { CheckoutPlan } from '@klab-berlin/api-sdk/lib/types/requests/Billing';
import { STRIPE_PLAN_DISPLAY } from '../../constants/stripePlanBenefits';
import StripePlanBenefits from './StripePlanBenefits';

interface StripeUpgradeOptionCardProps {
  targetPlanKey: CheckoutPlan;
  // Per-month price of the target plan, in cents. From kimuno's
  // `getUpgradeOptions.options[].newMonthlyPrice`.
  newMonthlyPrice: number;
  onConfirm: () => void;
  isSubmitting?: boolean;
  isDisabled?: boolean;
  // SM-391: highlights this card as the anchor tier — green frame + centered
  // "Am beliebtesten" chip, mirroring the Zuora `/premium` PricingPackage badge.
  isMostPopular?: boolean;
}

const formatMonthlyPrice = (priceCents: number): string =>
  (priceCents / 100).toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Middle column of the Stripe upgrade row — one card per reachable upgrade
// target. Displays the target plan's per-month price as "X,YY€ mtl." with a
// "bis zum Ende deines Abos" subtitle and a "Jetzt wechseln" primary CTA,
// mirroring the existing `/premium` pricing-page card.
const StripeUpgradeOptionCard: React.FC<StripeUpgradeOptionCardProps> = ({
  targetPlanKey,
  newMonthlyPrice,
  onConfirm,
  isSubmitting = false,
  isDisabled = false,
  isMostPopular = false,
}) => {
  const { t } = useTranslation();
  const display = STRIPE_PLAN_DISPLAY[targetPlanKey];
  const priceAmount = formatMonthlyPrice(newMonthlyPrice);

  return (
    <Box
      data-testid='stripe-upgrade-option-card'
      data-target-plan-key={targetPlanKey}
      sx={{
        width: customSizes.pricingPackage.width,
        display: 'flex',
        flexDirection: 'column',
        gap: spaces.xl,
        border: `1px solid ${isMostPopular ? palette.success.main : palette.elevation.outlined}`,
        borderRadius: borderRadius.xl,
        backgroundColor: palette.common.white,
        position: 'relative',
        padding: spaces.lg,
        opacity: isDisabled ? 0.5 : 1,
      }}
    >
      {isMostPopular && (
        <Chip
          label={t('Most popular')}
          color='success'
          size='medium'
          variant='filled'
          data-testid='most-popular-chip'
          sx={{
            height: 34,
            position: 'absolute',
            top: '-18px', // half of this Chip height + parent container padding
            left: '50%',
            transform: 'translateX(-50%)',
            '.MuiChip-label': {
              fontWeight: 500,
            },
          }}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          height: '80px',
        }}
      >
        <Typography
          variant='h4'
          color={palette.text.primary}
          sx={{ fontWeight: 600 }}
        >
          {t(display.packageName)}
        </Typography>
        <Typography variant='body2' color={palette.text.secondary}>
          {t(display.description)}
        </Typography>
      </Box>
      <Box data-testid='stripe-upgrade-option-price'>
        <Typography
          data-testid='stripe-upgrade-option-price-amount'
          variant='h2'
          color={palette.text.primary}
          sx={{ fontWeight: 500 }}
        >
          {t('stripeUpgrade.priceMonthly', { amount: priceAmount })}
        </Typography>
        <Typography
          variant='caption'
          color={palette.text.secondary}
          sx={{ display: 'inline-block' }}
        >
          {t('Until the end of your subscription')}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: spaces.xs,
        }}
      >
        <Button
          data-testid='stripe-upgrade-option-confirm'
          variant='contained'
          color='primary'
          size='large'
          disabled={isDisabled || isSubmitting}
          sx={{
            width: '100%',
            fontWeight: 500,
            '&:hover': { color: palette.primary.contrastText },
          }}
          onClick={onConfirm}
        >
          {t('Switch now')}
        </Button>
      </Box>
      {display.bonusBenefits.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: spaces.xl,
          }}
        >
          <Typography variant='overline' color={palette.text.primary}>
            {t('Everything you currently have, plus:')}
          </Typography>
          {display.bonusBenefits.map((section, index) => (
            <StripePlanBenefits key={index} benefit={section} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default StripeUpgradeOptionCard;
